import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import CardCourseWithLesson from "components/Card/CardCourseWithLesson";
import useTranslation from "next-translate/useTranslation";
import IconClose from "components/Icon/IconClose";
import { useAuthStore } from "modules/Auth/authStore";
import { isBrowser } from "lib/ssr";
import { getSubDomain, useIsSubdomain } from "lib/host";
import { getCookie } from "lib/helper";
import { SkeletonRect, SkeletonWrapper } from "components/Skeleton/Skeleton";
import { MoonLoader } from "react-spinners";
import { useIsDesktop } from "lib/device";
import { useGetCoursesByTenantSlug } from "./homeTenantApi";

const HomeCardKeywords: React.FC<{
  dataPaginate?: any;
  data?: any;
  isKeywordsSearched?: string;
  pageKeywords: any;
  setPageKeywords: (a: any) => void;
  hasNextPage: boolean;
  setHasNextPage: (a: boolean) => void;
  isLoadingKeywords: boolean;
  setIsLoadingKeywords: (a: boolean) => void;
  itemsPerPage?: number;
  startIndex?: number;
  keywordOthers?: any[];
  intObserver?: any;
  handleReset?: any;
  isSquare?: boolean;
  setCoursesAfterClicked?: (a: any) => void;
}> = ({
  dataPaginate,
  data,
  isKeywordsSearched,
  pageKeywords,
  setPageKeywords,
  hasNextPage,
  setHasNextPage,
  isLoadingKeywords,
  setIsLoadingKeywords,
  itemsPerPage,
  startIndex,
  keywordOthers,
  intObserver,
  handleReset,
  isSquare,
  setCoursesAfterClicked,
}) => {
  const { t } = useTranslation("common");

  const { currentUser } = useAuthStore((state) => ({
    currentUser: state.currentUser,
  }));
  const subDomain = isBrowser() ? getSubDomain(window.location.hostname) : null;
  const isDesktop = useIsDesktop();

  // @ts-ignore
  const getCourses = useGetCoursesByTenantSlug();
  let referral = useIsSubdomain()
    ? currentUser?.referral ?? subDomain
    : currentUser?.referral;

  const observerRef = useRef<IntersectionObserver>();
  const activeKeywordName = useMemo(
    () => keywordOthers.find((a) => a.active)?.name,
    [keywordOthers]
  );

  const referralCode =
    getCookie("ref_code_gk") && getCookie("ref_code_gk") !== "null"
      ? getCookie("ref_code_gk")
      : typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("referral")
      : "";

  const [currentPage, setCurrentPage] = useState(1);
  const [mergedData, setMergedData] = useState<any[]>([]);

  // Reset data ketika keyword berubah
  useEffect(() => {
    setMergedData(data);
    setCurrentPage(1);
  }, [activeKeywordName]);

  useEffect(() => {
    if (!hasNextPage || !activeKeywordName) return;

    setIsLoadingKeywords(true);

    getCourses.doFetch(
      {
        slug:
          window.location.hostname === "e-learning.bluebirdgroup.com"
            ? "bluebird"
            : window.location.hostname === "lms.injourney.id"
            ? "injourney"
            : referral,
        "others[]": [activeKeywordName],
        limit: itemsPerPage.toString(),
        // @ts-ignore
        page: currentPage.toString(),
      },
      {
        onSuccess(data) {
          const paginate = (data as any).meta.pagination;

          // Gabungkan data baru dengan data sebelumnya
          setMergedData((prev) => {
            // Filter untuk menghindari duplikat berdasarkan ID
            const newData = data.data.filter(
              (newItem: any) => !prev.some((item) => item.id === newItem.id)
            );
            return currentPage === 1 ? data.data : [...prev, ...newData];
          });

          setHasNextPage(paginate.current_page < paginate.total_pages);
          setIsLoadingKeywords(false);
        },
        onError(error) {
          setIsLoadingKeywords(false);
        },
      }
    );
  }, [currentPage, activeKeywordName, itemsPerPage, referral]);

  const lastCard = useCallback(
    (card) => {
      if (isLoadingKeywords) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((cards) => {
        if (cards[0].isIntersecting && hasNextPage) {
          setCurrentPage((prev) => prev + 1);
        }
      });

      if (card) observerRef.current.observe(card);
    },
    [isLoadingKeywords, hasNextPage]
  );

  const courseContent = mergedData?.map((course, index) => {
    const isLast = index === mergedData.length - 1;
    return (
      <div ref={isLast ? lastCard : null} key={`${course.id}-${index}`}>
        <CardCourseWithLesson
          {...course}
          isSquare={isSquare}
          tracker={{
            event: "b2b_homepage_course_a",
            property: {
              course_name: course?.name,
              course_slug: course?.slug,
              institution_name: course?.institution?.name,
              section: 3,
              category_name: `Default`,
              index,
              is_mandatory_course: false,
            },
          }}
        />
      </div>
    );
  });

  if (isLoadingKeywords && data.length === 0) {
    return (
      <>
        <div className="grid-cols-3 xl-max:grid-cols-2 hg-max:!grid-cols-1 gap-20 justify-center grid">
          {[...Array(3)].map((_, idx) => (
            <SkeletonWrapper key={idx}>
              <SkeletonRect className="w-full h-[214px] rounded-8" />
            </SkeletonWrapper>
          ))}
        </div>

        <div className="flex w-full p-24 items-center justify-center">
          <MoonLoader />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pb-24 flex justify-start items-center px-16 md:px-0 gap-8 mt-16">
        <div
          className="font-light items-center flex"
          dangerouslySetInnerHTML={{
            __html: t("descriptionResultSearch", {
              count: dataPaginate,
              text: isKeywordsSearched,
              additionalS: data?.length > 1 ? "s" : "",
            }),
          }}
        ></div>
        <div onClick={handleReset} className="cursor-pointer block">
          <IconClose size={20} color="#883CFF" />
        </div>
      </div>
      <div
        className={
          "grid-cols-3 xl-max:grid-cols-2 hg-max:!grid-cols-1 gap-20 justify-center grid"
        }
      >
        {courseContent}
      </div>

      {isLoadingKeywords && data.length > 0 && (
        <>
          <div className="grid-cols-3 xl-max:grid-cols-2 hg-max:!grid-cols-1 gap-20 justify-center grid">
            {[...Array(3)].map((_, idx) => (
              <SkeletonWrapper key={idx}>
                <SkeletonRect className="w-full h-[214px] rounded-8" />
              </SkeletonWrapper>
            ))}
          </div>

          <div className="flex w-full p-24 items-center justify-center">
            <MoonLoader />
          </div>
        </>
      )}
    </>
  );
};

export default HomeCardKeywords;
